/**
 * "You" on a festival map when Find is not running for that festival - someone going solo,
 * or with a group for a different event (operator, 2026-09-25).
 *
 * Deliberately separate from Find (useProximity): the two will merge one day, not now. It
 * owns its own location stream - on iOS the LynxLocation plugin with owner 'map', which the
 * plugin counts apart from Find's, so neither one stopping ever stops the other; on the web,
 * navigator.geolocation. Nothing is published: this position never leaves the phone.
 *
 * It only asks for permission when the festival is on (`mayPrompt`). Browsing a map from home
 * weeks out does not pop a location prompt; if permission was already given, you are shown.
 * The stream pauses while the app is in the background.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { NATIVE_OR_SIM } from '../lib/proximity/native/simPhone';
import { LynxLocation } from '../lib/proximity/native/LynxLocation';

const OWNER = 'map';
/** A fix worse than this says little about where on the grounds you are. */
const MAX_ACCURACY_M = 150;
/** Heading fires many times a second; only a turn this big is worth a render. */
const HEADING_STEP_DEG = 3;
/** iOS with Precise Location off reports fixes 1-3 km wide. Anything this loose means that. */
const APPROXIMATE_M = 1000;
/** A fix taken longer ago than this is CoreLocation's cache (often from home), not you now. */
const MAX_FIX_TAKEN_AGO_MS = 60 * 1000;
/**
 * The FIRST fix may be a little older: CoreLocation answers startUpdatingLocation with its last fix before a fresh
 * one, and on the grounds that last fix is you a minute or two ago. Taking it draws "you" the moment the map opens
 * instead of after GPS settles (operator, 2026-10-04: a visible wait before the avatar showed). It is dated from
 * when it was taken, so MAX_FIX_AGE_MS still retires it, and a fresh fix replaces it within seconds.
 */
const FIRST_FIX_TAKEN_AGO_MS = 2 * 60 * 1000;
/** Past this with no usable fix, "you" is where you were, not where you are: stop drawing it. */
const MAX_FIX_AGE_MS = 5 * 60 * 1000;
/** Back from the background after longer than this, the old fix is dropped at once. */
const FORGET_AFTER_HIDDEN_MS = 60 * 1000;

// Native start/stop calls run one after another. An effect re-run (mayPrompt flipping when the
// event loads) sent start, then the OLD run's stop landed after it and switched the map's GPS
// off (2026-09-25 audit).
let nativeQueue = Promise.resolve();
function queueNative(fn) {
  const run = nativeQueue.then(fn);
  nativeQueue = run.catch(() => {});
  return run;
}

const GRANTED = new Set(['whenInUse', 'always']);

export default function useMapSelfLocation({ enabled, mayPrompt }) {
  const posRef = useRef(null);
  // Bumped on every fix: the page's distances and Go gate are computed in render.
  const [fixAt, setFixAt] = useState(0);
  const [headingDeg, setHeadingDeg] = useState(null);
  const [approximate, setApproximate] = useState(false);
  // Location denied or restricted: the map can say so instead of silently never placing you (2026-10-04 audit).
  const [denied, setDenied] = useState(false);
  const [visible, setVisible] = useState(() => typeof document === 'undefined' || !document.hidden);
  const hiddenAtRef = useRef(null);

  useEffect(() => {
    const on = () => {
      if (document.hidden) hiddenAtRef.current = Date.now();
      else if (
        hiddenAtRef.current != null &&
        Date.now() - hiddenAtRef.current > FORGET_AFTER_HIDDEN_MS
      ) {
        // Half an hour in a pocket: the old "you" is not worth showing while a new fix comes in.
        posRef.current = null;
        setFixAt(0);
      }
      setVisible(!document.hidden);
    };
    document.addEventListener('visibilitychange', on);
    return () => document.removeEventListener('visibilitychange', on);
  }, []);

  useEffect(() => {
    if (!enabled || !visible) return undefined;
    let cancelled = false;
    const cleanups = [];
    const take = (lat, lng, accuracy, timestampMs) => {
      if (!Number.isFinite(lat) || !Number.isFinite(lng)) return;
      const takenAgo =
        Number.isFinite(timestampMs) && timestampMs > 1e12
          ? Math.max(0, Date.now() - timestampMs)
          : 0;
      if (takenAgo > (posRef.current ? MAX_FIX_TAKEN_AGO_MS : FIRST_FIX_TAKEN_AGO_MS)) return;
      if (Number.isFinite(accuracy))
        setApproximate((prev) =>
          prev === accuracy >= APPROXIMATE_M ? prev : accuracy >= APPROXIMATE_M,
        );
      if (Number.isFinite(accuracy) && accuracy > MAX_ACCURACY_M) return;
      posRef.current = {
        lat,
        lng,
        accuracyMeters: accuracy ?? null,
        headingDegrees: null,
        timestampMs: Date.now() - takenAgo,
      };
      setFixAt(Date.now());
    };

    if (NATIVE_OR_SIM) {
      (async () => {
        let status = 'unknown';
        try {
          ({ status } = await LynxLocation.getAuthorizationStatus());
        } catch {
          /* treat as unknown */
        }
        if (cancelled) return;
        setDenied(status === 'denied' || status === 'restricted');
        if (!GRANTED.has(status) && !(mayPrompt && status === 'notDetermined')) return;
        const loc = await LynxLocation.addListener('locationUpdate', (e) =>
          take(e.lat, e.lng, e.accuracy, e.timestampMs),
        );
        const head = await LynxLocation.addListener('headingUpdate', (e) => {
          const deg = e.trueHeading >= 0 ? e.trueHeading : e.magneticHeading;
          if (!Number.isFinite(deg)) return;
          setHeadingDeg((prev) =>
            prev == null || Math.abs(((deg - prev + 540) % 360) - 180) >= HEADING_STEP_DEG
              ? deg
              : prev,
          );
        });
        cleanups.push(
          () => loc.remove(),
          () => head.remove(),
        );
        if (cancelled) {
          cleanups.splice(0).forEach((fn) => {
            void fn();
          });
          return;
        }
        // startWatching asks for When In Use itself when the status is notDetermined.
        await queueNative(() => LynxLocation.startWatching({ owner: OWNER }));
        cleanups.push(() => queueNative(() => LynxLocation.stopWatching({ owner: OWNER })));
        if (cancelled)
          cleanups.splice(0).forEach((fn) => {
            void fn();
          });
      })().catch((err) => console.warn('[map] solo location failed:', err));
    } else if (typeof navigator !== 'undefined' && navigator.geolocation) {
      (async () => {
        let state = 'prompt';
        try {
          state = (await navigator.permissions?.query({ name: 'geolocation' }))?.state ?? 'prompt';
        } catch {
          /* Safari < 16 */
        }
        if (cancelled) return;
        setDenied(state === 'denied');
        if (state === 'denied' || (state !== 'granted' && !mayPrompt)) return;
        const id = navigator.geolocation.watchPosition(
          (p) => take(p.coords.latitude, p.coords.longitude, p.coords.accuracy, p.timestamp),
          () => {},
          { enableHighAccuracy: true, maximumAge: 5000 },
        );
        cleanups.push(() => navigator.geolocation.clearWatch(id));
        if (cancelled) cleanups.splice(0).forEach((fn) => fn());
      })();
    }

    return () => {
      cancelled = true;
      cleanups.splice(0).forEach((fn) => {
        void fn();
      });
    };
  }, [enabled, visible, mayPrompt]);

  // Find taking over stops this stream but keeps the last fix: the page falls back to it until the Find engine
  // has its own, so "you" does not vanish for the hand-off (2026-10-04). MAX_FIX_AGE_MS still retires it, and
  // leaving the map unmounts the hook.
  useEffect(() => {
    if (!enabled) {
      setHeadingDeg(null);
      setApproximate(false);
    }
  }, [enabled]);

  // A fix that has not been refreshed in minutes (every new one too loose to use, or GPS lost)
  // is no longer where you are.
  const getSelfPosition = useCallback(() => {
    const p = posRef.current;
    return p && Date.now() - p.timestampMs <= MAX_FIX_AGE_MS ? p : null;
  }, []);
  // Dev preview only: ?approximate shows the Precise Location prompt without an iPhone.
  const devApproximate =
    import.meta.env.DEV &&
    typeof window !== 'undefined' &&
    new URLSearchParams(window.location.search).has('approximate');
  const shownApproximate = approximate || devApproximate;
  return useMemo(
    () => ({ getSelfPosition, headingDeg, fixAt, approximate: shownApproximate, denied }),
    [getSelfPosition, headingDeg, fixAt, shownApproximate, denied],
  );
}
